import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, {Dayjs} from 'dayjs';
import NetworkService from '@app/services/network/NetworkService';
import {API_PATH, HTTP_OK} from '@app/common/constants';
import {ToasterService} from '@app/services';
import {ToastType} from '@app/services/toaster';
import {
  TableData,
  NDSAuditApiResponse,
  transformNDSAuditResponse,
} from './ndsAuditDetailsMapper';

export const NDSAuditDetailsScreen = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGo = async () => {
    if (!searchText.trim()) return;

    setLoading(true);
    setHasSearched(true);
    setTableData([]);

    try {
      const url =
        API_PATH.moduleSpecificUtilities.ndsAuditDetails.getNDSAuditDetails;
      const params: Record<string, any> = {
        employeeCode: searchText.trim(),
      };
      if (selectedDate) {
        params.ndsDate = selectedDate.format('DD-MMM-YYYY');
      }
      const response: any = await NetworkService.get(url, params, {});
      if (response?.status === HTTP_OK && response?.data) {
        const apiData: NDSAuditApiResponse = response.data;
        const mapped = transformNDSAuditResponse(apiData);
        setTableData(mapped);
      } else {
        ToasterService.showToaster(
          response?.data?.message || 'Failed to fetch NDS Audit Details',
          ToastType.ERROR,
        );
      }
    } catch (error) {
      ToasterService.showToaster(
        'Something went wrong while fetching NDS Audit Details',
        ToastType.ERROR,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGo();
    }
  };

  const renderTable = (table: TableData) => (
    <Box key={table.tableName} sx={{mb: 3}}>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontSize: 16,
          fontWeight: '600',
          color: '#322b7c',
          mb: 1,
          mt: 2,
        }}>
        {table.tableName}
      </Typography>
      <Divider sx={{mb: 1}} />
      {table.rows.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{boxShadow: 1, borderRadius: 1, maxHeight: 400}}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {table.columns.map((col: string) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: '600',
                      fontSize: 12.7,
                      color: '#322b7c',
                      backgroundColor: '#f5f5f5',
                      whiteSpace: 'nowrap',
                    }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row: Record<string, any>, rowIndex: number) => (
                <TableRow key={rowIndex} sx={{boxShadow: 1}}>
                  {table.columns.map((col: string) => (
                    <TableCell
                      key={`${rowIndex}-${col}`}
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: 12.7,
                        color: '#1c1939',
                      }}>
                      {row[col] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: 14,
            color: '#666',
            textAlign: 'center',
            py: 3,
          }}>
          No Records Found
        </Typography>
      )}
    </Box>
  );

  const renderNoRecordsPlaceholder = () => (
    <Box sx={{textAlign: 'center', mt: 6}}>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontSize: 14,
          color: '#666',
        }}>
        No Records Found
      </Typography>
    </Box>
  );

  const renderEmptyTables = () => (
    <Box sx={{mt: 2}}>
      {['NDS', 'Daily Actual Activity', 'DCR','NDS Party Details'].map(name => (
        <Box key={name} sx={{mb: 3}}>
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: 16,
              fontWeight: '600',
              color: '#322b7c',
              mb: 1,
              mt: 2,
            }}>
            {name}
          </Typography>
          <Divider sx={{mb: 1}} />
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              py: 3,
            }}>
            No Records Found
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{mt: 2}}>
      {/* Search, Date Picker, Go Button Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}>
        <TextField
          size="small"
          placeholder="Search SAP Code"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={handleKeyPress}
          sx={{
            width: 320,
            '& .MuiInputBase-input': {
              fontFamily: 'Poppins',
              fontSize: 13,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{color: '#999'}} />
              </InputAdornment>
            ),
          }}
          data-testid="nds-search-input"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedDate}
            inputFormat="DD/MM/YYYY"
            onChange={(newValue: Dayjs | null) => setSelectedDate(newValue)}
            renderInput={(params: any) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  width: 180,
                  '& .MuiInputBase-input': {
                    fontFamily: 'Poppins',
                    fontSize: 13,
                  },
                }}
                data-testid="nds-date-picker"
              />
            )}
          />
        </LocalizationProvider>

        <Button
          variant="contained"
          onClick={handleGo}
          disabled={!searchText.trim() || loading}
          sx={{
            backgroundColor: '#322b7c',
            color: '#fff',
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: 14,
            textTransform: 'none',
            px: 4,
            height: 35,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#251f6b',
            },
            '&.Mui-disabled': {
              backgroundColor: '#a8a4c9',
              color: '#fff',
            },
          }}
          data-testid="nds-go-button">
          {loading ? (
            <CircularProgress size={22} sx={{color: '#fff'}} />
          ) : (
            'GO'
          )}
        </Button>
      </Box>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', mt: 4}}>
          <CircularProgress sx={{color: '#322b7c'}} />
        </Box>
      )}

      {/* Tables Section */}
      {!loading &&
        (tableData.length > 0
          ? tableData.map(renderTable)
          : hasSearched
          ? renderEmptyTables()
          : renderNoRecordsPlaceholder())}
    </Box>
  );
};
